'use client';
import { createClient } from '@/lib/supabase/client';

/**
 * Community Service - Handles all community hub operations with real-time support
 */

// =============================================
// POST OPERATIONS
// =============================================

/**
 * Fetch all community posts with real-time subscription
 */
export async function fetchPosts({ category = null, type = null, limit = 20 } = {}) {
  const supabase = createClient();

  try {
    let query = supabase?.from('community_posts')?.select(`
        *,
        author:author_id(id, email, raw_user_meta_data),
        post_reactions(reaction_type, user_id),
        post_comments(id),
        post_shares(id)
      `)?.eq('is_archived', false)?.order('is_pinned', { ascending: false })?.order('created_at', { ascending: false })?.limit(limit);

    if (category) {
      query = query?.eq('category', category);
    }

    if (type) {
      query = query?.eq('post_type', type);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(post => ({
      ...post,
      author: {
        id: post?.author?.id,
        name: post?.author?.raw_user_meta_data?.full_name || post?.author?.email?.split('@')?.[0],
        avatar: post?.author?.raw_user_meta_data?.avatar_url || '/api/placeholder/40/40',
        alt: `${post?.author?.raw_user_meta_data?.full_name || 'User'} profile picture`,
        roles: post?.author?.raw_user_meta_data?.roles || ['Member']
      },
      reactions: {
        amen: post?.post_reactions?.filter(r => r?.reaction_type === 'amen')?.length || 0,
        prayer: post?.post_reactions?.filter(r => r?.reaction_type === 'prayer')?.length || 0,
        heart: post?.post_reactions?.filter(r => r?.reaction_type === 'heart')?.length || 0
      },
      comments: post?.post_comments?.length || 0,
      shares: post?.post_shares?.length || 0
    })) || [];
  } catch (error) {
    console.error('Error fetching posts:', error);
    throw error;
  }
}

/**
 * Create a new community post
 */
export async function createPost({ content, type, category, tags = [], media = [] }) {
  const supabase = createClient();

  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('community_posts')?.insert({
        author_id: user?.id,
        content,
        post_type: type,
        category,
        tags,
        media_urls: media
      })?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
}

/**
 * Add reaction to a post
 */
export async function addReaction({ postId, reactionType }) {
  const supabase = createClient();

  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('post_reactions')?.upsert({
        post_id: postId,
        user_id: user?.id,
        reaction_type: reactionType
      })?.select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding reaction:', error);
    throw error;
  }
}

/**
 * Remove reaction from a post
 */
export async function removeReaction({ postId, reactionType }) {
  const supabase = createClient();

  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase?.from('post_reactions')?.delete()?.eq('post_id', postId)?.eq('user_id', user?.id)?.eq('reaction_type', reactionType);

    if (error) throw error;
  } catch (error) {
    console.error('Error removing reaction:', error);
    throw error;
  }
}

/**
 * Add comment to a post
 */
export async function addComment({ postId, content, parentCommentId = null }) {
  const supabase = createClient();

  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('post_comments')?.insert({
        post_id: postId,
        author_id: user?.id,
        content,
        parent_comment_id: parentCommentId
      })?.select(`
        *,
        author:author_id(id, email, raw_user_meta_data)
      `)?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
}

/**
 * Fetch comments for a post with real-time subscription
 */
export async function fetchComments(postId) {
  const supabase = createClient();

  try {
    const { data, error } = await supabase?.from('post_comments')?.select(`
        *,
        author:author_id(id, email, raw_user_meta_data)
      `)?.eq('post_id', postId)?.eq('is_deleted', false)?.order('created_at', { ascending: true });

    if (error) throw error;

    return data?.map(comment => ({
      ...comment,
      author: {
        id: comment?.author?.id,
        name: comment?.author?.raw_user_meta_data?.full_name || comment?.author?.email?.split('@')?.[0],
        avatar: comment?.author?.raw_user_meta_data?.avatar_url || '/api/placeholder/40/40',
        alt: `${comment?.author?.raw_user_meta_data?.full_name || 'User'} profile picture`
      }
    })) || [];
  } catch (error) {
    console.error('Error fetching comments:', error);
    throw error;
  }
}

// =============================================
// PRAYER WALL OPERATIONS
// =============================================

/**
 * Fetch prayer requests with real-time subscription
 */
export async function fetchPrayerRequests({ category = null, status = 'active', limit = 20 } = {}) {
  const supabase = createClient();

  try {
    let query = supabase?.from('prayer_requests')?.select(`
        *,
        author:author_id(id, email, raw_user_meta_data),
        prayer_interactions(id, user_id),
        prayer_updates(id, content, created_at)
      `)?.eq('status', status)?.order('created_at', { ascending: false })?.limit(limit);

    if (category) {
      query = query?.eq('category', category);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(prayer => ({
      ...prayer,
      author: prayer?.is_anonymous ? {
        id: 'anonymous',
        name: 'Anonymous',
        avatar: '/api/placeholder/40/40',
        alt: 'Anonymous user'
      } : {
        id: prayer?.author?.id,
        name: prayer?.author?.raw_user_meta_data?.full_name || prayer?.author?.email?.split('@')?.[0],
        avatar: prayer?.author?.raw_user_meta_data?.avatar_url || '/api/placeholder/40/40',
        alt: `${prayer?.author?.raw_user_meta_data?.full_name || 'User'} profile picture`
      },
      prayedCount: prayer?.prayer_interactions?.length || 0,
      updates: prayer?.prayer_updates || []
    })) || [];
  } catch (error) {
    console.error('Error fetching prayer requests:', error);
    throw error;
  }
}

/**
 * Create a new prayer request
 */
export async function createPrayerRequest({ title, body, category, isAnonymous = false }) {
  const supabase = createClient();

  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('prayer_requests')?.insert({
        author_id: isAnonymous ? null : user?.id,
        title,
        body,
        category,
        is_anonymous: isAnonymous
      })?.select()?.single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating prayer request:', error);
    throw error;
  }
}

/**
 * Mark that user prayed for a request
 */
export async function prayForRequest(prayerId) {
  const supabase = createClient();

  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase?.from('prayer_interactions')?.upsert({
        prayer_id: prayerId,
        user_id: user?.id
      })?.select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error praying for request:', error);
    throw error;
  }
}

// =============================================
// LIVE VIEWER COUNTS
// =============================================

/**
 * Fetch live viewer counts for streaming channels
 */
export async function fetchLiveViewerCounts() {
  const supabase = createClient();

  try {
    const { data, error } = await supabase?.from('now_playing')?.select(`
        channel_id,
        show_title,
        channels(name, channel_status)
      `)?.eq('channels.channel_status', 'live');

    if (error) throw error;

    // In a real implementation, viewer counts would come from streaming analytics
    // For now, we'll simulate with random counts
    return data?.map(stream => ({
      channelId: stream?.channel_id,
      channelName: stream?.channels?.name,
      showTitle: stream?.show_title,
      viewerCount: Math.floor(Math.random() * 500) + 50 // Simulated viewer count
    })) || [];
  } catch (error) {
    console.error('Error fetching viewer counts:', error);
    throw error;
  }
}

// =============================================
// REAL-TIME SUBSCRIPTIONS
// =============================================

/**
 * Subscribe to real-time post updates
 */
export function subscribeToPostUpdates(callback) {
  const supabase = createClient();

  const subscription = supabase?.channel('community_posts_channel')?.on('postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'community_posts'
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return subscription;
}

/**
 * Subscribe to real-time comment updates for a specific post
 */
export function subscribeToCommentUpdates(postId, callback) {
  const supabase = createClient();

  const subscription = supabase?.channel(`post_comments_${postId}`)?.on('postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'post_comments',
        filter: `post_id=eq.${postId}`
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return subscription;
}

/**
 * Subscribe to real-time prayer request updates
 */
export function subscribeToPrayerUpdates(callback) {
  const supabase = createClient();

  const subscription = supabase?.channel('prayer_requests_channel')?.on('postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'prayer_requests'
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return subscription;
}

/**
 * Subscribe to real-time prayer interaction updates
 */
export function subscribeToPrayerInteractions(callback) {
  const supabase = createClient();

  const subscription = supabase?.channel('prayer_interactions_channel')?.on('postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'prayer_interactions'
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return subscription;
}

/**
 * Subscribe to real-time live viewer count updates
 */
export function subscribeToViewerCounts(callback) {
  const supabase = createClient();

  const subscription = supabase?.channel('now_playing_channel')?.on('postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'now_playing'
      },
      (payload) => {
        callback(payload);
      }
    )?.subscribe();

  return subscription;
}
